import DailyImageryIcon from '@apps-common/ui-icons/DailyImagery.jsx';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import {Box, Divider, Paper, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import React, {Fragment, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';

import {EmptyMessageContainer} from '../../ui/message-container.jsx';
import {LeftDrawer} from '../drawers.jsx';
import LeftDrawerHeader from '../left-drawer-header.jsx';
import ActionsMenu from './actions-menu/index.jsx';
import FiltersPanel from './filters/index.jsx';
import DailyResultsPanel from './results-panel/index.jsx';
import {focusItem} from '@explorer/actions/focused-item';
import {fitExtent} from '@explorer/actions/map';
import {setActiveResultId} from '@explorer/actions/sidebar';
import {setTileMode} from '@explorer/actions/tiles';
import {showLeftDrawer} from '@explorer/actions/ui';
import {DRAWERS, TILE_MODE, TOUR_STAGES} from '@explorer/constants';
import {selectFilteredResultsList} from '@explorer/selectors/results';

const Sidebar = ({
  activeResultId,
  mode,
  focusItem,
  results,
  resultsLoading,
  setActiveResultId,
  tileMode,
  setTileMode,
  open,
  fitExtent,
  ...props
}) => {
  const {t} = useTranslation();

  const getDailyModePanels = () => {
    if (!resultsLoading) {
      if (!props.geometry) {
        return (
          <EmptyMessageContainer
            icon={<LocationOnOutlinedIcon fontSize="inherit" />}
          >
            <Typography variant="h6">
              {t(
                'search.chooseLocationMessage',
                'Choose a location to search through our catalogue of daily imagery.'
              )}
            </Typography>
          </EmptyMessageContainer>
        );
      } else if (props.geometryTooLarge) {
        return (
          <EmptyMessageContainer>
            <LocationOnOutlinedIcon fontSize="large" color="inherit" />
            <Typography variant="h6">
              {t(
                'search.drawSmallerGeoMessage',
                'Draw a smaller AOI to search through our catalogue of daily imagery.'
              )}
            </Typography>
          </EmptyMessageContainer>
        );
      }
    }

    return <DailyResultsPanel results={results} />;
  };

  // ToDo: should this even be here?
  useEffect(() => {
    if (
      (activeResultId && resultsLoading && results.length === 0) ||
      tileMode !== TILE_MODE.RESULTS
    ) {
      setActiveResultId(null);
      focusItem(null);
    }
  }, [
    activeResultId,
    mode,
    results,
    resultsLoading,
    setActiveResultId,
    focusItem,
    tileMode,
  ]);

  useEffect(() => {
    if (open) {
      if (tileMode !== TILE_MODE.RESULTS) {
        setTileMode(TILE_MODE.RESULTS);
      }
    }
  }, [open, setTileMode, tileMode]);

  return (
    <LeftDrawer
      slotProps={{
        paper: {
          'data-tour': TOUR_STAGES.SEARCH_SIDEBAR,
        },
      }}
      open={open}
    >
      <Fragment>
        <Box
          sx={{
            display: props.showFiltersPanel ? 'flex' : 'none',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <FiltersPanel />
        </Box>

        <Box
          data-cy="sidebar-content"
          sx={{
            display: 'flex',
            visibility: props.showFiltersPanel ? 'hidden' : 'visible',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <LeftDrawerHeader
            title={t('search.titleScenes')}
            icon={<DailyImageryIcon color="action" />}
          />
          <Paper
            elevation={2}
            sx={theme => ({
              ...theme.applyStyles('light', {
                backgroundColor: 'grey.100',
              }),
              boxShadow: 'none',
            })}
          >
            <ActionsMenu />
          </Paper>
          <Divider />
          {getDailyModePanels()}
        </Box>
      </Fragment>
    </LeftDrawer>
  );
};

Sidebar.propTypes = {
  activeResultId: PropTypes.string,
  focusItem: PropTypes.func,
  mode: PropTypes.string.isRequired,
  open: PropTypes.bool,
  results: PropTypes.array,
  resultsLoading: PropTypes.bool,
  setActiveResultId: PropTypes.func,
  showFiltersPanel: PropTypes.bool,
  showLeftDrawer: PropTypes.func,
  geometry: PropTypes.object,
  geometryTooLarge: PropTypes.bool,
  setTileMode: PropTypes.func,
  tileMode: PropTypes.string,
  fitExtent: PropTypes.func,
  searchName: PropTypes.string,
};

const mapStateToProps = state => ({
  activeResultId: state.sidebar.activeResultId,
  mode: state.ui.mode,
  resultsLoading: state.results.loading,
  showFiltersPanel: state.sidebar.showFiltersPanel,
  open: state.ui.drawers.left === DRAWERS.SEARCH,
  geometry: state.search.geometry,
  searchName: state.search.name,
  geometryTooLarge: state.search.geometryTooLarge,
  tileMode: state.tiles.mode,
  results: selectFilteredResultsList(state),
});

const mapDispatchProps = {
  focusItem,
  setActiveResultId,
  showLeftDrawer,
  setTileMode,
  fitExtent,
};

export default connect(mapStateToProps, mapDispatchProps)(Sidebar);
